/**
 * Renders the Engraving Mask result onto a 2D canvas context.
 * Pure drawing logic: no Vue, no refs.
 */

import {
  clipToHeartPath,
  strokeHeartPath,
  clipHeartHalfPlane,
} from './heartSvgPath'
import {
  clipToRectSvgPath,
  strokeRectSvgPath,
  getRectSvgCanvasBounds,
} from './rectangleSvgPath'
import { OVAL_RX_RY_RATIO, SHAPE_PADDING, STROKE_STYLE, STROKE_WIDTH } from './constants'

function drawImageInHalf(ctx, img, params, cx, cy) {
  const { x, y, rotation, zoom } = params
  const rad = (rotation * Math.PI) / 180
  ctx.translate(cx + x, cy + y)
  ctx.rotate(rad)
  ctx.scale(zoom, zoom)
  const iw = img.naturalWidth || img.width
  const ih = img.naturalHeight || img.height
  ctx.drawImage(img, -iw / 2, -ih / 2, iw, ih)
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {Object} opts
 * @param {number} opts.size - canvas size (width/height)
 * @param {string} opts.shape - 'circle' | 'heart' | 'oval' | 'rectangle-wide' | 'rectangle-square'
 * @param {number} opts.numParts - 1, 2, or 3
 * @param {string} opts.direction - 'vertical' | 'horizontal'
 * @param {number} opts.spacing - pixels between parts
 * @param {HTMLImageElement[]} opts.images - [image1, image2, image3]
 * @param {Object[]} opts.params - [{x,y,rotation,zoom}, ...]
 * @param {boolean} [opts.includeStroke=true] - if false, no border (e.g. for final export)
 */
export function renderEngravingMask(ctx, opts) {
  const { size: s, shape, numParts, direction, spacing: spacingPx, images, params, includeStroke = true } = opts
  const [img1, img2, img3] = images || []
  const [p1, p2, p3] = params || []
  const cx = s / 2
  const cy = s / 2
  // Same padding for all; shapes almost fill container (innerSize = s - 2*padding).
  const innerSize = s - 2 * SHAPE_PADDING
  const R = innerSize / 2
  const isHeart = shape === 'heart'
  const isOval = shape === 'oval'
  const isRectWide = shape === 'rectangle-wide'
  const isRectSquare = shape === 'rectangle-square'
  const isRectangle = isRectWide || isRectSquare
  const rectShapeKey = isRectWide ? 'rectangle-wide' : isRectSquare ? 'rectangle-square' : null
  const Rx = isOval ? R * OVAL_RX_RY_RATIO : R
  const Ry = isOval ? R : R
  const rectBounds =
    isRectangle && rectShapeKey ? getRectSvgCanvasBounds(cx, cy, innerSize, rectShapeKey) : null
  const rectLeft = rectBounds ? rectBounds.left : cx - R
  const rectRight = rectBounds ? rectBounds.right : cx + R
  const rectTop = rectBounds ? rectBounds.top : cy - R
  const rectBottom = rectBounds ? rectBounds.bottom : cy + R
  const threeParts = numParts === 3
  const isHorizontal = direction === 'vertical' // vertical direction => horizontal strips

  ctx.clearRect(0, 0, s, s)
  ctx.globalAlpha = 1
  ctx.globalCompositeOperation = 'source-over'

  const clipShape = (context = ctx) => {
    context.beginPath()
    if (isOval) context.ellipse(cx, cy, Rx, Ry, 0, 0, Math.PI * 2)
    else context.arc(cx, cy, R, 0, Math.PI * 2)
  }

  const strokeShape = (context = ctx) => {
    context.beginPath()
    if (isOval) context.ellipse(cx, cy, Rx, Ry, 0, 0, Math.PI * 2)
    else context.arc(cx, cy, R, 0, Math.PI * 2)
  }

  const spacing = Math.max(0, spacingPx || 0)

  // ----- 1 part: single image clipped to shape -----
  if (numParts === 1) {
    if (img1) {
      ctx.save()
      if (isHeart) clipToHeartPath(ctx, cx, cy, innerSize)
      else if (isRectangle) clipToRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey)
      else {
        clipShape()
        ctx.clip()
      }
      drawImageInHalf(ctx, img1, p1, cx, cy)
      ctx.restore()
    }
    if (includeStroke) {
      if (isHeart) strokeHeartPath(ctx, cx, cy, innerSize, STROKE_STYLE, STROKE_WIDTH)
      else if (isRectangle) {
        strokeRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey, STROKE_STYLE, STROKE_WIDTH)
      } else {
        ctx.save()
        ctx.strokeStyle = STROKE_STYLE
        ctx.lineWidth = STROKE_WIDTH
        strokeShape()
        ctx.stroke()
        ctx.restore()
      }
    }
    return
  }

  // ----- 3 parts: draw to offscreen then once to main -----
  if (threeParts) {
    const off = document.createElement('canvas')
    off.width = s
    off.height = s
    const octx = off.getContext('2d')
    octx.globalAlpha = 1
    octx.globalCompositeOperation = 'source-over'

    if (isHorizontal) {
      const top = isRectangle ? rectTop : isOval ? cy - Ry : cy - R
      const bottom = isRectangle ? rectBottom : isOval ? cy + Ry : cy + R
      const stripH = (bottom - top) / 3
      const [y1, y2, y3, y4] = [top, top + stripH, top + stripH * 2, bottom]
      const drawStrip = (img, params, yTop, yBottom) => {
        if (!img) return
        octx.save()
        if (isHeart) clipToHeartPath(octx, cx, cy, innerSize)
        else if (isRectangle) clipToRectSvgPath(octx, cx, cy, innerSize, rectShapeKey)
        else {
          octx.beginPath()
          clipShape(octx)
          octx.clip()
        }
        octx.beginPath()
        octx.rect(0, yTop, s, yBottom - yTop)
        octx.clip()
        drawImageInHalf(octx, img, params, cx, cy)
        octx.restore()
      }
      drawStrip(img1, p1, y1, y2)
      drawStrip(img2, p2, y2, y3)
      drawStrip(img3, p3, y3, y4)
    } else {
      const left = isRectangle ? rectLeft : isOval ? cx - Rx : cx - R
      const right = isRectangle ? rectRight : isOval ? cx + Rx : cx + R
      const stripW = (right - left) / 3
      const [x1, x2, x3, x4] = [left, left + stripW, left + stripW * 2, right]
      const drawStrip = (img, params, xLeft, xRight) => {
        if (!img) return
        octx.save()
        if (isHeart) clipToHeartPath(octx, cx, cy, innerSize)
        else if (isRectangle) clipToRectSvgPath(octx, cx, cy, innerSize, rectShapeKey)
        else {
          octx.beginPath()
          clipShape(octx)
          octx.clip()
        }
        octx.beginPath()
        octx.rect(xLeft, 0, xRight - xLeft, s)
        octx.clip()
        drawImageInHalf(octx, img, params, cx, cy)
        octx.restore()
      }
      drawStrip(img1, p1, x1, x2)
      drawStrip(img2, p2, x2, x3)
      drawStrip(img3, p3, x3, x4)
    }

    ctx.drawImage(off, 0, 0)

    if (spacing > 0) {
      const half = spacing / 2
      ctx.save()
      if (isHeart) clipToHeartPath(ctx, cx, cy, innerSize)
      else if (isRectangle) clipToRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey)
      else {
        clipShape()
        ctx.clip()
      }
      if (isHorizontal) {
        const top = isRectangle ? rectTop : isOval ? cy - Ry : cy - R
        const stripH = ((isRectangle ? rectBottom : isOval ? cy + Ry : cy + R) - top) / 3
        const y2 = top + stripH
        const y3 = top + stripH * 2
        ctx.clearRect(0, y2 - half, s, spacing)
        ctx.clearRect(0, y3 - half, s, spacing)
      } else {
        const left = isRectangle ? rectLeft : isOval ? cx - Rx : cx - R
        const stripW = ((isRectangle ? rectRight : isOval ? cx + Rx : cx + R) - left) / 3
        const x2 = left + stripW
        const x3 = left + stripW * 2
        ctx.clearRect(x2 - half, 0, spacing, s)
        ctx.clearRect(x3 - half, 0, spacing, s)
      }
      ctx.restore()
    }

    if (includeStroke) {
      if (isHeart) strokeHeartPath(ctx, cx, cy, innerSize, STROKE_STYLE, STROKE_WIDTH)
      else if (isRectangle) {
        strokeRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey, STROKE_STYLE, STROKE_WIDTH)
      } else {
        ctx.save()
        ctx.strokeStyle = STROKE_STYLE
        ctx.lineWidth = STROKE_WIDTH
        strokeShape()
        ctx.stroke()
        ctx.restore()
      }
    }
    return
  }

  // ----- 2 parts: horizontal strips (top/bottom) -----
  if (isHorizontal) {
    const top = isRectangle ? rectTop : isOval ? cy - Ry : cy - R
    const bottom = isRectangle ? rectBottom : isOval ? cy + Ry : cy + R
    const midY = (top + bottom) / 2
    if (isHeart || isRectangle) {
      const drawHalf = (img, params, yTop, yBottom) => {
        if (!img) return
        ctx.save()
        if (isHeart) clipToHeartPath(ctx, cx, cy, innerSize)
        else clipToRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey)
        ctx.beginPath()
        ctx.rect(0, yTop, s, yBottom - yTop)
        ctx.clip()
        drawImageInHalf(ctx, img, params, cx, cy)
        ctx.restore()
      }
      drawHalf(img1, p1, top, midY)
      drawHalf(img2, p2, midY, bottom)
    } else {
      const drawHalf = (img, params, yTop, yBottom) => {
        if (!img) return
        ctx.save()
        ctx.beginPath()
        if (isOval) ctx.ellipse(cx, cy, Rx, Ry, 0, 0, Math.PI * 2)
        else ctx.arc(cx, cy, R, 0, Math.PI * 2)
        ctx.clip()
        ctx.beginPath()
        ctx.rect(0, yTop, s, yBottom - yTop)
        ctx.clip()
        drawImageInHalf(ctx, img, params, cx, cy)
        ctx.restore()
      }
      drawHalf(img1, p1, top, midY)
      drawHalf(img2, p2, midY, bottom)
    }
    if (spacing > 0) {
      ctx.save()
      if (isHeart) clipToHeartPath(ctx, cx, cy, innerSize)
      else if (isRectangle) clipToRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey)
      else {
        clipShape()
        ctx.clip()
      }
      ctx.clearRect(0, midY - spacing / 2, s, spacing)
      ctx.restore()
    }
    if (includeStroke) {
      if (isHeart) strokeHeartPath(ctx, cx, cy, innerSize, STROKE_STYLE, STROKE_WIDTH)
      else if (isRectangle) {
        strokeRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey, STROKE_STYLE, STROKE_WIDTH)
      } else {
        ctx.save()
        ctx.strokeStyle = STROKE_STYLE
        ctx.lineWidth = STROKE_WIDTH
        strokeShape()
        ctx.stroke()
        ctx.restore()
      }
    }
    return
  }

  // ----- 2 parts: vertical strips (left/right) -----
  if (isHeart) {
    if (img1) {
      ctx.save()
      clipToHeartPath(ctx, cx, cy, innerSize)
      clipHeartHalfPlane(ctx, cx, cy, innerSize, true, spacing, s)
      drawImageInHalf(ctx, img1, p1, cx, cy)
      ctx.restore()
    }
    if (img2) {
      ctx.save()
      clipToHeartPath(ctx, cx, cy, innerSize)
      clipHeartHalfPlane(ctx, cx, cy, innerSize, false, spacing, s)
      drawImageInHalf(ctx, img2, p2, cx, cy)
      ctx.restore()
    }
    if (includeStroke) {
      strokeHeartPath(ctx, cx, cy, innerSize, STROKE_STYLE, STROKE_WIDTH)
    }
  } else if (isRectangle) {
    const clipSvgShape = (c) => clipToRectSvgPath(c, cx, cy, innerSize, rectShapeKey)
    const halfSpacing = spacing > 0 ? spacing / 2 : 0
    const leftEdge = cx - halfSpacing
    const rightEdge = cx + halfSpacing
    if (img1) {
      ctx.save()
      clipSvgShape(ctx)
      ctx.beginPath()
      ctx.rect(0, 0, leftEdge, s)
      ctx.clip()
      drawImageInHalf(ctx, img1, p1, cx, cy)
      ctx.restore()
    }
    if (img2) {
      ctx.save()
      clipSvgShape(ctx)
      ctx.beginPath()
      ctx.rect(rightEdge, 0, s - rightEdge, s)
      ctx.clip()
      drawImageInHalf(ctx, img2, p2, cx, cy)
      ctx.restore()
    }
    if (spacing > 0) {
      ctx.save()
      clipSvgShape(ctx)
      ctx.clearRect(leftEdge, 0, spacing, s)
      ctx.restore()
    }
    if (includeStroke) {
      strokeRectSvgPath(ctx, cx, cy, innerSize, rectShapeKey, STROKE_STYLE, STROKE_WIDTH)
    }
  } else if (isOval) {
    if (img1) {
      ctx.save()
      ctx.beginPath()
      ctx.ellipse(cx, cy, Rx, Ry, 0, Math.PI / 2, (Math.PI * 3) / 2)
      ctx.closePath()
      ctx.clip()
      drawImageInHalf(ctx, img1, p1, cx, cy)
      ctx.restore()
    }
    if (img2) {
      ctx.save()
      ctx.beginPath()
      ctx.ellipse(cx, cy, Rx, Ry, 0, -Math.PI / 2, Math.PI / 2)
      ctx.closePath()
      ctx.clip()
      drawImageInHalf(ctx, img2, p2, cx, cy)
      ctx.restore()
    }
    if (spacing > 0) {
      ctx.save()
      ctx.beginPath()
      ctx.ellipse(cx, cy, Rx, Ry, 0, 0, Math.PI * 2)
      ctx.clip()
      ctx.clearRect(cx - spacing / 2, cy - Ry - 1, spacing, Ry * 2 + 2)
      ctx.restore()
    }
    if (includeStroke) {
      ctx.save()
      ctx.strokeStyle = STROKE_STYLE
      ctx.lineWidth = STROKE_WIDTH
      ctx.beginPath()
      ctx.ellipse(cx, cy, Rx, Ry, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }
  } else {
    if (img1) {
      ctx.save()
      ctx.beginPath()
      ctx.arc(cx, cy, R, Math.PI / 2, (Math.PI * 3) / 2)
      ctx.closePath()
      ctx.clip()
      drawImageInHalf(ctx, img1, p1, cx, cy)
      ctx.restore()
    }
    if (img2) {
      ctx.save()
      ctx.beginPath()
      ctx.arc(cx, cy, R, -Math.PI / 2, Math.PI / 2)
      ctx.closePath()
      ctx.clip()
      drawImageInHalf(ctx, img2, p2, cx, cy)
      ctx.restore()
    }
    if (spacing > 0) {
      ctx.save()
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.clip()
      ctx.clearRect(cx - spacing / 2, cy - R - 1, spacing, R * 2 + 2)
      ctx.restore()
    }
    if (includeStroke) {
      ctx.save()
      ctx.strokeStyle = STROKE_STYLE
      ctx.lineWidth = STROKE_WIDTH
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }
  }
}
